import { useMemo, useRef } from "react";
import { useGridCubes3d } from "./GridCubes3dContext";
import {
    GRID_CUBES_ARROW_MARKER_ID,
    drawablesNeedConnectionMarkers,
    ndcMaskOverlayRect,
} from "../../utils/gridCubesGeometry";
import { roundDeg } from "../../utils/primitivePointsPlacement";
import { useSvgViewBoxZoom } from "../../hooks/useSvgViewBoxZoom";

const PHASE_LABELS = {
    populate: "Populating grid...",
    connect: "Routing connections...",
    project: "Projecting faces...",
} as const;

export function GridCubes3dViewer() {
    const {
        drawables,
        objectCount,
        connectionCount,
        estimatedObjectCount,
        computePending,
        computeProgress,
        generate,
        cancelCompute,
        canExport,
        handleDownload,
        viewBoxX,
        viewBoxY,
        viewBoxWidth,
        viewBoxHeight,
        maskPreviewEnabled,
        maskPreviewUrl,
        maskPreviewPending,
        projectionNdcBounds,
        maskSvgs,
        faceStrokeEnabled,
        faceStrokeColor,
        faceStrokeWidth,
        rotationXDeg,
        rotationYDeg,
        setRotationXDeg,
        setRotationYDeg,
    } = useGridCubes3d();
    const needsConnectionMarkers = useMemo(
        () => drawablesNeedConnectionMarkers(drawables),
        [drawables],
    );

    const dragRef = useRef<{
        pointerId: number;
        startX: number;
        startY: number;
        startRotX: number;
        startRotY: number;
    } | null>(null);

    const base = useMemo(
        () => ({
            x: viewBoxX,
            y: viewBoxY,
            w: viewBoxWidth,
            h: viewBoxHeight,
        }),
        [viewBoxX, viewBoxY, viewBoxWidth, viewBoxHeight],
    );

    const {
        svgRef,
        viewBoxAttr: viewBox,
        panHandlers,
        zoomPercent,
        zoomIn,
        zoomOut,
        resetView,
    } = useSvgViewBoxZoom({ base });

    const maskOverlayRect = useMemo(() => {
        if (!projectionNdcBounds) return null;
        return ndcMaskOverlayRect(
            projectionNdcBounds,
            viewBoxX,
            viewBoxY,
            viewBoxWidth,
            viewBoxHeight,
        );
    }, [projectionNdcBounds, viewBoxX, viewBoxY, viewBoxWidth, viewBoxHeight]);

    const progressText = useMemo(() => {
        if (!computeProgress) return "Computing...";
        const label = PHASE_LABELS[computeProgress.phase];
        if (computeProgress.total <= 0) return label;
        const pct = Math.round(
            (computeProgress.processed / computeProgress.total) * 100,
        );
        return `${label} (${Math.max(0, Math.min(100, pct))}%)`;
    }, [computeProgress]);

    const handlePointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
        panHandlers.onPointerDown(event); // middle-button pan (button 1)
        if (event.button !== 0) return;
        dragRef.current = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            startRotX: rotationXDeg,
            startRotY: rotationYDeg,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
    };

    const handlePointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
        panHandlers.onPointerMove(event); // middle-button pan
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        const dx = event.clientX - drag.startX;
        const dy = event.clientY - drag.startY;
        const sensitivity = 0.45;
        setRotationYDeg(roundDeg(drag.startRotY + dx * sensitivity));
        setRotationXDeg(roundDeg(drag.startRotX + dy * sensitivity));
    };

    const handlePointerEnd = (event: React.PointerEvent<SVGSVGElement>) => {
        panHandlers.onPointerUp(event); // middle-button pan release
        const drag = dragRef.current;
        if (!drag || drag.pointerId !== event.pointerId) return;
        dragRef.current = null;
        try {
            event.currentTarget.releasePointerCapture(event.pointerId);
        } catch {
            // no-op
        }
    };

    return (
        <div className="panel-viewer path-populate-viewer">
            <div className="path-tool-preview">
                <div className="svg-zoom-controls">
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomOut}
                        title="Zoom out"
                    >
                        −
                    </button>
                    <span className="zoom-level">{zoomPercent}%</span>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={zoomIn}
                        title="Zoom in"
                    >
                        +
                    </button>
                    <button
                        type="button"
                        className="zoom-button"
                        onClick={resetView}
                        title="Reset zoom"
                    >
                        ⌂
                    </button>
                </div>
                <div className="svg-scroll-wrapper">
                    <div className="svg-container svg-container--fill">
                        <svg
                            ref={svgRef}
                            viewBox={viewBox}
                            xmlns="http://www.w3.org/2000/svg"
                            className="path-tool-svg"
                            onPointerDown={handlePointerDown}
                            onPointerMove={handlePointerMove}
                            onPointerUp={handlePointerEnd}
                            onPointerCancel={handlePointerEnd}
                            style={{ cursor: "grab", touchAction: "none" }}
                        >
                            {needsConnectionMarkers && (
                                <defs>
                                    <marker
                                        id={GRID_CUBES_ARROW_MARKER_ID}
                                        markerWidth={8}
                                        markerHeight={8}
                                        refX={7}
                                        refY={4}
                                        orient="auto"
                                        markerUnits="strokeWidth"
                                    >
                                        <path
                                            d="M0,0 L8,4 L0,8 z"
                                            fill="context-stroke"
                                        />
                                    </marker>
                                </defs>
                            )}
                            {maskPreviewEnabled &&
                                maskPreviewUrl &&
                                maskOverlayRect && (
                                    <image
                                        href={maskPreviewUrl}
                                        x={maskOverlayRect.x}
                                        y={maskOverlayRect.y}
                                        width={maskOverlayRect.width}
                                        height={maskOverlayRect.height}
                                        preserveAspectRatio="none"
                                        pointerEvents="none"
                                    />
                                )}
                            {drawables.map((d, index) => {
                                if (d.kind === "face") {
                                    const pts = d.points
                                        .map((p) => `${p.x},${p.y}`)
                                        .join(" ");
                                    return (
                                        <polygon
                                            key={index}
                                            points={pts}
                                            fill={d.color}
                                            stroke={
                                                faceStrokeEnabled
                                                    ? faceStrokeColor
                                                    : "none"
                                            }
                                            strokeWidth={
                                                faceStrokeEnabled
                                                    ? faceStrokeWidth
                                                    : undefined
                                            }
                                            strokeLinejoin="round"
                                        />
                                    );
                                }
                                return (
                                    <line
                                        key={index}
                                        x1={d.x1}
                                        y1={d.y1}
                                        x2={d.x2}
                                        y2={d.y2}
                                        stroke={d.color}
                                        strokeWidth={d.strokeWidth}
                                        strokeLinecap="round"
                                        strokeDasharray={d.strokeDasharray}
                                        markerStart={
                                            d.markerStart
                                                ? `url(#${GRID_CUBES_ARROW_MARKER_ID})`
                                                : undefined
                                        }
                                        markerEnd={
                                            d.markerEnd
                                                ? `url(#${GRID_CUBES_ARROW_MARKER_ID})`
                                                : undefined
                                        }
                                    />
                                );
                            })}
                        </svg>
                    </div>
                </div>
                <div className="path-tool-footer">
                    <p className="path-tool-meta">
                        {computePending
                            ? progressText
                            : [
                                  `${objectCount} objects, ${connectionCount} connections - est. ${estimatedObjectCount}`,
                                  maskPreviewEnabled && maskSvgs.length > 0
                                      ? maskPreviewPending
                                          ? "mask preview updating..."
                                          : "mask preview on"
                                      : null,
                              ]
                                  .filter(Boolean)
                                  .join(" · ")}
                    </p>
                    <div className="path-tool-footer-actions">
                        {computePending ? (
                            <button
                                type="button"
                                className="generic-button"
                                onClick={cancelCompute}
                            >
                                Cancel
                            </button>
                        ) : (
                            <button
                                type="button"
                                className="generic-button"
                                onClick={() => {
                                    void generate();
                                }}
                            >
                                Generate
                            </button>
                        )}
                        <button
                            type="button"
                            className="generic-button"
                            disabled={!canExport}
                            onClick={handleDownload}
                        >
                            Download result SVG
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
